<template>
  <div id="chr-trafficlights" class="tw-flex tw-flex-col tw-gap-10">
    <div class="tw-flex tw-flex-col tw-gap-4 !tw-break-inside-avoid">
      <div class="tw-flex tw-items-center tw-justify-between tw-h-[44px]">
        <div
          id="chr-sheading-categories"
          class="tw-text-[24px] tw-text-[#211F58] tw-font-semibold tw-w-fit"
        >
          {{
            getComponentUiText("app-share-cyber-profile-traffic-lights-title-1")
          }}
        </div>
        <div
          v-if="isEnableEdit"
          id="pen-icon-traffic"
          :class="isEdit ? 'tw-bg-[#211F58]' : 'tw-bg-[#FFF]'"
          class="header__pen tw-cursor-pointer"
          @click="editNotes"
        >
          <Pen :color="isEdit ? '#ffffff' : '#387DFF'" />
        </div>
      </div>
      <div
        class="tw-select-none tw-text-sm sm:tw-w-[60%] tw-font-normal tw-text-[#231F20] tw-opacity-70"
      >
        {{
          getComponentUiText(
            "app-share-cyber-profile-traffic-lights-description-1",
          )
        }}
      </div>
    </div>
    <CustomTable
      :columns="tableFields"
      :data="data"
      :show-checkbox-row="false"
      :skeleton-row-number="1"
      header-wrapper-class="table-header"
      :custom-class="`tls-vulnerabilities-table-wrap traficlights-table ${
        isEdit ? 'traficlights-table-edit' : ''
      }`"
      :is-loading="isLoading"
      :empty-text="
        getComponentUiText(
          'app-scan-scanning-types-item-tls-result-table-empty-1',
        )
      "
    >
      <template #cell(checklistCategoryLabel)="{ value }">
        <span
          class="tw-py-[3px] tw-px-[5px] tw-leading-[130%] tw-text-[13px] tw-font-medium tw-text-[#387DFF] tw-bg-[#E4F1FF] tw-rounded-[4px]"
        >
          {{ value }}
        </span>
      </template>
      <template #cell(checklistCategoryNotes)="value">
        <div
          v-if="!isEdit"
          :id="`chr-trafficlights-notes-${value.item.checklistCategoryId}`"
          class="tw-p-[12px] tw-text-[13px] tw-font-normal tw-leading-none tw-text-[#231F20] tw-bg-[#EFF0F1] tw-rounded-md"
          :class="isEdit ? 'tw-bg-[#EFF0F1]' : 'tw-bg-[#FFF] tw-px-[0px]'"
        >
          {{ value.item.checklistCategoryNotes }}
        </div>
        <div
          v-if="isEdit"
          class="tw-flex tw-items-center tw-w-full tw-relative"
        >
          <div
            class="tw-flex tw-text-[13px] tw-whitespace-nowrap tw-h-[44px] tw-w-[100%]"
          >
            <input
              :id="`traffic-input-${value.item.checklistCategoryId}`"
              v-model="value.item.checklistCategoryNotes"
              type="text"
              :class="`${isCallApi ? '!tw-pr-[45px]' : ''}`"
              class="tw-p-[12px] tw-text-[13px] tw-font-normal notes-input-field"
              @focus="
                () => {
                  value.item.checklistCategoryNotesOld =
                    value.item.checklistCategoryNotes;
                }
              "
              @change="updateNotes(value.item)"
              @keyup.enter="editNotes"
              @keyup.tab="keyTabEvent(value.item)"
              @focusout="
                () => {
                  onFocusout(value);
                }
              "
            />
          </div>
          <div
            v-if="
              isCallApi &&
              trafficLightsEditing.checklistCategoryOrder ===
                value.item.checklistCategoryOrder
            "
            class="tw-h-[35px] tw-w-[35px] tw-absolute tw-right-[12px]"
          >
            <!-- eslint-disable vue/no-unused-refs -->
            <LottieAnimation
              ref="anim"
              :animation-data="ProgressAnimation"
              :auto-play="true"
              :loop="true"
            />
          </div>
        </div>
      </template>
      <template #cell(checklistCategoryScore)="{ value }">
        <div class="tw-flex tw-items-center tw-justify-center tw-gap-[12px]">
          <div
            class="tw-w-[12px] tw-h-[12px] tw-rounded-[100%]"
            :class="`${
              value * 100 >= 0 && value * 100 < 33.33
                ? 'tw-bg-[#F7553C]'
                : 'tw-bg-[#DBDBDB]'
            }`"
          />
          <div
            class="tw-w-[12px] tw-h-[12px] tw-rounded-[100%]"
            :class="`${
              value * 100 >= 33.33 && value * 100 < 75
                ? 'tw-bg-[#FE9A05]'
                : 'tw-bg-[#DBDBDB]'
            }`"
          />
          <div
            class="tw-w-[12px] tw-h-[12px] tw-rounded-[100%]"
            :class="`${
              value * 100 >= 75 ? 'tw-bg-[#01893D]' : 'tw-bg-[#DBDBDB]'
            }`"
          />
        </div>
      </template>
    </CustomTable>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import Pen from "../svgs/pen.vue";
import { getComponentUiText } from "@/utils/utilities";
import CustomTable from "@/components/common/customTable.vue";
import store from "@/store";
import { LottieAnimation } from "lottie-web-vue";
import ProgressAnimation from "@/assets/animation/progress_light.json";

const props = defineProps({
  isLoading: Boolean,
  isEdit: {
    type: Boolean,
    default: false,
  },
  isEnableEdit: {
    type: Boolean,
    default: true,
  },
  isCallApi: {
    type: Boolean,
    default: false,
  },
  trafficLightsEditing: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["update:edit", "on-focus-out"]);

const editNotes = () => {
  emit("update:edit", !props.isEdit);
};

const onFocusout = (value) => {
  listItemChanged.value = [];
  if (value && value.idx === data.value.length - 1) {
    value.idx = -1;
  }
  emit("on-focus-out", value);
};

const data = computed(() => {
  return store.getters["Sharing/getChecklistCategoriesData"];
});

const listItemChanged = ref([]);

const updateNotes = (value) => {
  listItemChanged.value.push(value);

  listItemChanged.value.filter(
    (item, index, self) =>
      index ===
      self.findIndex((t) => t.checklistCategoryId === item.checklistCategoryId),
  );
};

watch(
  () => props.isEdit,
  (value) => {
    if (!value) {
      store.dispatch(
        "Sharing/updateChecklistTrafficLight",
        listItemChanged.value.filter(
          (item, index, self) =>
            index ===
            self.findIndex(
              (t) => t.checklistCategoryId === item.checklistCategoryId,
            ),
        ),
      );
    } else {
      listItemChanged.value = [];
    }
  },
);

const keyTabEvent = (item) => {
  // store.dispatch("Sharing/updateChecklistTrafficLight", [item]);
};

const tableFields = [
  {
    text: getComponentUiText(
      "app-share-cyber-profile-traffic-lights-table-category-header-title-1",
    ),
    value: "checklistCategoryLabel",
    headerClass:
      "!tw-text-[#fff] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase",
    sortable: false,
    valueClass: "!tw-pr-[45px] !tw-align-center tw-h-[70px] tw-w-[15%]",
  },
  {
    text: getComponentUiText(
      "app-share-cyber-profile-traffic-lights-table-notes-header-title-1",
    ),
    value: "checklistCategoryNotes",
    headerClass:
      "!tw-text-[#fff] tw-text-[10px] tw-pr-[45px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase tw-justify-left",
    sortable: false,
    valueClass: "!tw-pr-[45px] !tw-align-center tw-w-[50%] !tw-py-0",
  },
  {
    text: getComponentUiText(
      "app-share-cyber-profile-traffic-lights-table-status-header-title-1",
    ),
    value: "checklistCategoryScore",
    headerClass:
      "!tw-text-[#fff] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase",
    sortable: false,
    valueClass: "!tw-align-center tw-w-[10%] !tw-pr-[25px]",
  },
];
</script>

<style scoped lang="scss">
:deep(.table-header) {
  height: 46px;
  background-color: #211f58 !important;
  &:first-of-type {
    padding-left: 25px !important;
  }
}

.header__pen {
  border-radius: 6px;
  border: 1px solid var(--UI-rule, #eff0f1);
  padding: 9px 14px;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.05);
  width: 46px;
  height: 44px;
}
.notes-input-field {
  border-radius: 6px;
  border: 1px solid var(--UI-rule, #eff0f1);
  background: rgba(239, 240, 241, 0.5);
  color: #231f20;
  width: 100%;
}

@media print {
  #pen-icon-traffic {
    display: none;
  }
}
</style>
